import gsap, { Power2, Power3, Power4 } from "gsap";
import { Howl } from 'howler';

import { GetBy } from '../_app/cuchillo/core/Element';
import Keyboard, { KEYS } from '../_app/cuchillo/core/Keyboard';
import { Functions } from "../_app/cuchillo/utils/Functions";
import { Metrics } from "../_app/cuchillo/core/Metrics";
import { Maths } from "../_app/cuchillo/utils/Maths";
import { isSmartphone } from "../_app/cuchillo/core/Basics";

export default class Footer {

  static container = GetBy.id("Footer");
  static logo = GetBy.class("logo", Footer.container)[0];
  static clients = GetBy.class("clients", Footer.container)[0];
  static contact = GetBy.class("contact", Footer.container)[0];
  static legal = GetBy.class("Footer__legal", Footer.container)[0];
  static isBlack = false;
  static _color = "#1A1A1A";
  static logoItems;
  static clientsItems;
  static contactItems;
  static yShow = isSmartphone? -250 : -200;
  static _distance = 0;
  static isPlaying = false;
  static soundId = null;
  static isFixed = false;
  static delay = .1;

  static sound = new Howl({
    src: ['/assets/audio/mixkit-discrete-door-bell-announcement-225.wav'],
    volume: 1,
    preload: true,
    html5: false,
    onload: () => {
      console.log("🎧 Sonido precargado");
    },
    onloaderror: (id, err) => {
      console.error("❌ Error al cargar el sonido", err);
    },
    onend: () => {
      Footer.isPlaying = false;
      Footer.soundId = null;
    }
  });

  static initSound() {
    Footer.sound.pos(0, 0, Footer._distance);
    Footer.sound.pannerAttr({
      panningModel: 'HRTF',
      distanceModel: 'inverse',
      refDistance: 1,
      maxDistance: 10000,
      rolloffFactor: 1,
      coneInnerAngle: 360,
      coneOuterAngle: 0,
      coneOuterGain: 0
    });
  }

  static get color() {
    return Footer._color;
  }

  static set color(__color) {
    Footer._color = __color;
    Footer.logo.style.setProperty("--color", Footer.color);
  }

  static get distance() {
    return Footer._distance;
  }

  static set distance(z) {
    Footer._distance = z;
    Footer.sound.pos(0, 0, z);
  }

  static init() {
    Footer.setupLogo();
    Footer.setupClients();
    Footer.setupContact();
    Footer.initSound();

    Keyboard.add(KEYS.UP, "up", () => { Footer.show(); });
    Keyboard.add(KEYS.DOWN, "down", () => { Footer.hide(); });
  }

  static show() {
    Footer.showLogo();
    Footer.showClients();
    Footer.showContact();
    Footer.goBlack(false, 2, .4);
    Footer.playSoundOnce();
  }

  static hide() {
    Footer.hideLogo();
    Footer.hideClients();
    Footer.hideContact();
    Footer.goGrey();
    Footer.stopSound();
  }

  static playSoundOnce() {
    if (Footer.isPlaying) return;

    Footer.isPlaying = true;
    Footer.soundId = Footer.sound.play();
  }

  static stopSound() {
    Footer.sound.stop();
    Footer.soundId = null;
    Footer.isPlaying = false;
  }


  static setupLogo() {
    Footer.logoItems = [...GetBy.selector("svg", Footer.logo)];
  }

  static setupClients() {
    Footer.clientsItems = [...GetBy.selector("li", Footer.clients)];

    for (let i = 0; i < Footer.clientsItems.length; i++) {
      const item = Footer.clientsItems[i];
      gsap.killTweensOf(item);
      gsap.set(item, {
        opacity: 0
      });
    }
  }

  static setupContact() {
    Footer.contactItems = [...GetBy.selector("a", Footer.contact)];

    for (let i = 0; i < Footer.contactItems.length; i++) {
      const item = Footer.contactItems[i];
      gsap.killTweensOf(item);
      gsap.set(item, {
        opacity: 0
      });
    }
  }

  static showContact() {
    gsap.killTweensOf(Footer.contact);
    gsap.killTweensOf(Footer.legal);

    gsap.to(Footer.contact, {
      y: Footer.yShow,
      duration: 1,
      delay: Footer.delay,
      ease: Power4.easeOut
    });

    gsap.to(Footer.legal, {
      opacity: 1,
      duration: .3,
      delay: Footer.delay,
      ease: Power2.easeOut
    });

    Footer.contactItems = Functions.arrayRandom(Footer.contactItems);

    for (let i = 0; i < Footer.contactItems.length; i++) {
      const item = Footer.contactItems[i];
      let delay = Footer.delay + .4 + i * .1;
      let distance = Maths.maxminRandom(Metrics.WIDTH * .009, Metrics.WIDTH * .007);

      const offset = {
        x: Math.random() > .5 ? 1 : -1,
        y: Math.random() > .5 ? -1 : 1
      }

      gsap.killTweensOf(item);
      gsap.set(item, { x: offset.x * distance * 2, y: offset.y * distance, force3D: true });

      gsap.to(item, { x: 0, duration: .3, delay: delay, ease: Power3.easeOut });
      gsap.to(item, { y: 0, duration: .4, delay: delay + .1, ease: Power3.easeInOut });
      gsap.to(item, { opacity: 1, duration: .1, delay: delay, ease: Power2.easeIn });
    }
  }

  static hideContact() {
    gsap.killTweensOf(Footer.contact);
    gsap.killTweensOf(Footer.legal);

    gsap.to(Footer.contact, {
      y: 0,
      duration: .2,
      ease: Power2.easeOut,
    });

    gsap.to(Footer.legal, {
      opacity: 0,
      duration: .1,
      ease: Power2.easeOut
    });

    Footer.contactItems = Functions.arrayRandom(Footer.contactItems);

    for (let i = 0; i < Footer.contactItems.length; i++) {
      const item = Footer.contactItems[i];
      gsap.killTweensOf(item);
      gsap.to(item, {
        opacity: 0,
        duration: .1,
        delay: i * .01,
        ease: Power2.easeIn
      });
    }
  }

  static showClients() {
    gsap.killTweensOf(Footer.clients);
    gsap.to(Footer.clients, {
      y: isSmartphone? Footer.yShow * .75 : Footer.yShow * .65,
      duration: 1,
      ease: Power4.easeOut,
      delay: Footer.delay
    });

    for (let i = 0; i < Footer.clientsItems.length; i++) {
      const item = Footer.clientsItems[i];
      gsap.killTweensOf(item);
      gsap.to(item, {
        opacity: 1,
        duration: .1,
        delay: Footer.delay + i * .05,
        ease: Power2.easeIn
      });
    }
  }

  static hideClients() {
    gsap.killTweensOf(Footer.clients);
    gsap.to(Footer.clients, {
      y: 0,
      duration: .2,
      ease: Power2.easeOut
    });

    for (let i = 0; i < Footer.clientsItems.length; i++) {
      const item = Footer.clientsItems[i];
      gsap.killTweensOf(item);
      gsap.to(item, {
        opacity: 0,
        duration: .1,
        delay: i * .01,
        ease: Power2.easeIn
      });
    }
  }

  static showLogo() {
    gsap.killTweensOf(Footer.distance);
    Footer.distance = 0;
    for (let i = 0; i < Footer.logoItems.length; i++) {
      const svg = Footer.logoItems[i];
      gsap.killTweensOf(svg);
      gsap.to(svg, {
        y: Footer.yShow,
        duration: 1 + i * 0.01,
        delay: Footer.delay + i * 0.01,
        ease: Power4.easeOut
      });
    }
  }

  static hideLogo() {
    for (let i = 0; i < Footer.logoItems.length; i++) {
      const svg = Footer.logoItems[i];
      gsap.killTweensOf(svg);
      gsap.to(svg, {
        y: 0,
        duration: 0.8 + i * 0.05,
        ease: Power4.easeOut
      });
    }
  }

  static fixedBlack() {
    if (Footer.isBlack) return;
    Footer.goBlack();
  }

  static goBlack(_isFixed = false, __time = .2, __delay = 0) {
    if (Footer.isFixed && !_isFixed) return;

    Footer.isFixed = _isFixed;

    if (!Footer.isFixed) {
      gsap.to([Footer.logoItems[0], Footer.logoItems[1]], {
        fill: "#1A1A1A",
        duration: .1,
        delay: .1,
        ease: Power3.easeIn
      });

      gsap.to([Footer.logoItems[2], Footer.logoItems[3], Footer.logoItems[4]], {
        fill: "#1A1A1A",
        duration: .1,
        delay: .5,
        ease: Power3.easeIn
      });

      gsap.to([Footer.logoItems[5]], {
        fill: "#1A1A1A",
        duration: .1,
        delay: 1.1,
        ease: Power3.easeIn
      });
    } else {
      for (let i = 0; i < 5; i++) {
        gsap.to([Footer.logoItems[i]], { fill: "#1A1A1A", duration: .3 + .1 * i, delay: 0, ease: Power3.easeIn });
      };
      gsap.to([Footer.logoItems[5]], { fill: "#1A1A1A", duration: .1, delay: .3 * 3, ease: Power3.easeIn });
    }
  }

  static goGrey(_isFixed = false, __time = .2, __delay = 0) {
    if (Footer.isFixed && !_isFixed) return;

    Footer.isFixed = false;

    if (Footer.isFixed) {
      gsap.killTweensOf(Footer);
      gsap.to(Footer.logoItems, {
        fill: "#f5f5f5",
        duration: __time,
        delay: __delay,
        ease: Power3.easeOut
      });
    } else {
      for (let i = 0; i < 5; i++) {
        gsap.to([Footer.logoItems[i]], { fill: "#f5f5f5", duration: .3 + .1 * i, delay: 0, ease: Power3.easeIn });
        if (i == 1) {
          gsap.to([Footer.logoItems[5]], { fill: "#f5f5f5", duration: .3, delay: .3 + .1 * i, ease: Power3.easeIn });
        }
      };
    }
  }

  static resize() {
    // Adaptación a viewport si necesario
  }
}
